import { useState } from "react";

// Pricing page — Phase 3
//
// Two subscription tiers (Standard / Pro). Lifetime $29.99 offer is fully
// retired — no buy button anywhere. Existing Founder Lifetime users keep
// their grandfathered access; new users see only the subscription options.
//
// Each Buy button POSTs to /api/create-checkout with a `productType` matching
// what the webhook expects (sub_standard, sub_pro).

const TIERS = [
  {
    type: "sub_standard",
    name: "Standard",
    price: "$7.99",
    period: "/month",
    description: "For creators testing the digital products waters",
    cta: "Start with Standard",
    highlighted: false,
    features: [
      "50 credits per month",
      "Each search costs 1 credit (10 topics per search)",
      "Each outline costs 0.5 credits",
      "Sellability, virality, competition scores",
      "Full PDF chapter outline + download",
      "Save & export favourites",
      "Cancel anytime",
    ],
  },
  {
    type: "sub_pro",
    name: "Pro",
    price: "$12.99",
    period: "/month",
    description: "For serious sellers shipping multiple PDFs",
    cta: "Go Pro",
    highlighted: true,
    badge: "Most Popular",
    features: [
      "100 credits per month — double the volume",
      "⚡ Priority generation queue",
      "Hide low-sellability topics (≥80 only filter)",
      "Everything in Standard",
      "Cancel anytime",
    ],
  },
];

export default function Pricing({ onLogin }) {
  const [loadingType, setLoadingType] = useState(null);

  const handleCheckout = async (productType) => {
    setLoadingType(productType);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productType }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // If the env vars for Stripe price IDs aren't set yet, we get a clear
      // 500 from the API with a descriptive error. Surface it instead of
      // failing silently.
      alert(data.error || "Something went wrong. Please try again.");
    } catch {
      alert("Something went wrong. Please try again.");
    }
    setLoadingType(null);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f5", fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .price-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(59,91,219,0.35) !important; }
        .price-cta-secondary:hover:not(:disabled) { background: #f4f4f0 !important; }
        @media (max-width: 720px) {
          .pricing-nav-tag { display: none !important; }
          .pricing-nav-guide { padding: 6px 8px !important; font-size: 13px !important; }
          .pricing-nav-cta { padding: 7px 12px !important; font-size: 13px !important; }
          .pricing-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Nav */}
      <nav style={{ background: "#fff", borderBottom: "1px solid #e8e8e4", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, gap: 12 }}>
        <button onClick={() => window.location.href = "/"} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, minWidth: 0 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>PDFTopicScout</span>
          <span className="pricing-nav-tag" style={{ fontSize: 12, color: "#3b5bdb", fontWeight: 600 }}>by Creatovate</span>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={() => window.location.href = "/guide"} className="pricing-nav-guide" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#444", fontFamily: "Inter, sans-serif", padding: "8px 14px" }}>Guide</button>
          <button onClick={onLogin} className="pricing-nav-cta" style={{ background: "transparent", color: "#555", border: "1px solid #e8e8e4", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Sign In</button>
        </div>
      </nav>

      {/* Header */}
      <div style={{ textAlign: "center", padding: "64px 24px 32px" }}>
        <h1 style={{ fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 900, color: "#1a1a1a", marginBottom: 12, letterSpacing: "-1px" }}>
          Find what people actually buy.
        </h1>
        <p style={{ fontSize: 18, color: "#666", maxWidth: 520, margin: "0 auto" }}>
          Two plans, monthly. Credits roll fresh every month. Cancel any time.
        </p>
      </div>

      {/* Tier grid */}
      <div className="pricing-grid" style={{
        maxWidth: 880, margin: "0 auto 56px", padding: "0 20px",
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20,
      }}>
        {TIERS.map(t => (
          <div key={t.type} style={{
            background: "#fff",
            borderRadius: 20,
            border: t.highlighted ? "2px solid #3b5bdb" : "1px solid #e8e8e4",
            padding: "36px 32px",
            boxShadow: t.highlighted ? "0 12px 48px rgba(59,91,219,0.15)" : "0 2px 8px rgba(0,0,0,0.03)",
            position: "relative",
            display: "flex", flexDirection: "column",
          }}>
            {t.badge && (
              <div style={{
                position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                background: "#3b5bdb", color: "#fff",
                borderRadius: 20, padding: "4px 18px",
                fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", letterSpacing: "0.04em",
              }}>
                {t.badge}
              </div>
            )}

            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>{t.name}</div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>{t.description}</div>

              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, marginBottom: 4 }}>
                <span style={{ fontSize: 44, fontWeight: 900, color: "#1a1a1a", lineHeight: 1 }}>{t.price}</span>
                <span style={{ fontSize: 16, color: "#999", fontWeight: 600, marginBottom: 6 }}>{t.period}</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 28, flex: 1 }}>
              {t.features.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{
                    width: 20, height: 20, background: t.highlighted ? "#eef1fd" : "#f4f4f0",
                    borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
                  }}>
                    <span style={{ color: t.highlighted ? "#3b5bdb" : "#666", fontSize: 11, fontWeight: 800 }}>✓</span>
                  </div>
                  <span style={{ fontSize: 14, color: "#333", fontWeight: 500, lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => handleCheckout(t.type)}
              disabled={loadingType !== null}
              className="price-cta"
              style={{
                width: "100%",
                background: t.highlighted ? "#3b5bdb" : "#1a1a1a",
                color: "#fff", border: "none",
                borderRadius: 12, padding: "14px",
                fontWeight: 700, fontSize: 15,
                cursor: loadingType ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                fontFamily: "Inter, sans-serif",
                boxShadow: t.highlighted ? "0 4px 16px rgba(59,91,219,0.25)" : "none",
                opacity: loadingType && loadingType !== t.type ? 0.5 : 1,
              }}
            >
              {loadingType === t.type ? "Redirecting…" : t.cta + " →"}
            </button>
          </div>
        ))}
      </div>

      {/* Trust strip */}
      <div style={{ textAlign: "center", marginBottom: 56, fontSize: 12, color: "#aaa", fontWeight: 500 }}>
        🔒 Secure Stripe checkout · Cancel any time · 7-day refund · No hidden fees
      </div>

      {/* FAQ */}
      <div style={{ maxWidth: 620, margin: "0 auto 80px", padding: "0 20px" }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1a1a1a", textAlign: "center", marginBottom: 24 }}>Common questions</h2>
        {[
          ["How do credits work?", "Each search you run costs 1 credit and produces 10 topic ideas. Each outline you generate costs 0.5 credits. Subscription credits refresh every month — they don't roll over, so use them or you lose them at renewal."],
          ["What's the difference between Standard and Pro?", "Pro doubles your monthly credits (100 vs 50), adds priority generation, and unlocks the 'hide low-sellability' filter so you only see the strongest picks. If you find yourself wanting to run more than 4-5 searches a week, Pro is the better deal."],
          ["Can I upgrade or downgrade?", "Yes — change plans any time. Stripe prorates the difference and we top up your credits to match the new tier."],
          ["Can I cancel?", "Anytime, no questions. You'll keep access through the end of your current billing period."],
          ["What if I need more credits in a month?", "You can buy a credit pack from the app anytime — 50 credits for $9.99 or 150 credits for $24.99. Pack credits roll over for a full year, so they're never wasted."],
          ["Can I get a refund?", "Yes — within 7 days of purchase, email info@creatovate.biz and we'll refund you in full."],
        ].map(([q, a], i) => (
          <div key={i} style={{ borderBottom: "1px solid #e8e8e4", padding: "18px 0" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>{q}</div>
            <div style={{ fontSize: 14, color: "#666", lineHeight: 1.7 }}>{a}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #e8e8e4", padding: "20px 28px", background: "#fff", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#999", marginBottom: 8 }}>
          <strong style={{ color: "#1a1a1a" }}>PDFTopicScout</strong> by <strong style={{ color: "#3b5bdb" }}>Creatovate</strong>
        </div>
        <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
          <a href="/privacy" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>Privacy Policy</a>
          <span style={{ color: "#ddd" }}>·</span>
          <a href="/terms" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>Terms of Service</a>
          <span style={{ color: "#ddd" }}>·</span>
          <a href="mailto:info@creatovate.biz" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>info@creatovate.biz</a>
        </div>
      </div>
    </div>
  );
}
